<template>
  <!-- The signed-in user's passkeys (#132), opened from the avatar menu. Wears
       ModalDialogForm, as PasskeyNudgeDialog does, so it reads as one of the
       app's own dialogs; the table goes in its slot. Not routed through the
       store-driven ModalDialog: the delete confirmation IS that dialog, and
       opens on top of this one. -->
  <div>
    <v-dialog
      :model-value="modelValue"
      :max-width="maxWidth"
      persistent
      @keydown.esc="!isAdding && close()"
    >
      <ModalDialogForm
        title="Passkeys"
        message="A passkey signs you in with your fingerprint, face or device PIN instead of a password. Add one on each device you use; a passkey saved to iCloud Keychain or Google Password Manager also works on your other devices."
        ok-button-caption-verb-override="Add a passkey"
        cancel-button-caption-verb-override="Close"
        :ok-button-width="200"
        :do-skip-verification="true"
        :max-width="maxWidth"
        :is-showing="modelValue"
        :is-processing="isAdding"
        :is-ok-disabled="!canCreatePasskey || isBusy"
        :do-subdue-message="true"
        data-testid="passkeys-dialog"
        @ok="addPasskey"
        @cancel="close"
      >
        <div class="mx-4">
          <div v-if="!canCreatePasskey" class="text-warning mb-4">
            This browser doesn't support passkeys.
          </div>
          <v-progress-linear v-if="isLoading" indeterminate />
          <v-table
            v-else-if="passkeys.length"
            class="pkd-table"
            data-testid="passkeys-table"
          >
            <!-- Fixed widths (table-layout: fixed, below). The centred columns
                 are sized to what they hold -- a date (2026-10-29 at most), or
                 "No"/an icon -- and the kebab column to its button; Name, the
                 only text of any length, takes the rest. Left to auto layout,
                 the spare width piled up in a wide gap before the kebab. -->
            <colgroup>
              <col />
              <col class="pkd-date-col" />
              <col class="pkd-date-col" />
              <col class="pkd-synced-col" />
              <col class="pkd-kebab-col" />
            </colgroup>
            <!-- TableCard's header bands, from the same helper (headerClass). -->
            <thead>
              <tr>
                <th :class="headerClass">Name</th>
                <th :class="headerClass" class="text-center">Added</th>
                <th :class="headerClass" class="text-center">Last used</th>
                <th :class="headerClass" class="text-center">Synced</th>
                <!-- Blank, as a TableCard's kebab column is. -->
                <th :class="headerClass" class="pkd-kebab-cell" />
              </tr>
            </thead>
            <tbody>
              <tr v-for="passkey in passkeys" :key="passkey.id">
                <td>{{ passkey.label }}</td>
                <td class="text-center">
                  {{ formatDate(passkey.createdAt) }}
                </td>
                <td class="text-center">
                  {{
                    passkey.lastUsedAt
                      ? formatDate(passkey.lastUsedAt)
                      : 'Never'
                  }}
                </td>
                <td class="text-center">
                  <v-icon
                    v-if="passkey.backedUp"
                    icon="mdi-cloud-check-outline"
                    aria-label="Synced"
                  />
                  <span v-else class="text-medium-emphasis">No</span>
                </td>
                <td class="pkd-kebab-cell">
                  <PasskeyMenu
                    :item="passkey"
                    :disabled="isBusy"
                    @rename="startRename"
                    @delete="confirmDelete"
                  />
                </td>
              </tr>
            </tbody>
          </v-table>
          <!-- ml-6: in line with the message above, whose text sits 24px
               further in than this slot (ModalDialogForm's v-card-text padding
               plus its ml-4). Greyed as the table's "No" is. -->
          <div v-else class="text-body-1 text-medium-emphasis ml-6">
            You have no passkeys yet.
          </div>
        </div>
      </ModalDialogForm>
    </v-dialog>

    <!-- A SIBLING of the dialog above, not nested in it: nested, its Esc also
         closed the passkeys dialog underneath. -->
    <v-dialog
      v-model="isRenaming"
      :max-width="renameMaxWidth"
      persistent
      @keydown.esc="!isSavingRename && cancelRename()"
    >
      <ModalDialogForm
        title="Rename passkey"
        message="Give it a name you'll recognize, such as the device it's on."
        ok-button-caption-verb-override="Save"
        :do-skip-verification="true"
        :max-width="renameMaxWidth"
        :is-showing="isRenaming"
        :is-processing="isSavingRename"
        :is-ok-disabled="!renameLabel.trim()"
        @ok="saveRename"
        @cancel="cancelRename"
      >
        <div class="pkd-rename-field mx-auto">
          <v-text-field
            v-model="renameLabel"
            label="Name"
            :counter="MAX_LABEL_LENGTH"
            :maxlength="MAX_LABEL_LENGTH"
            autofocus
            @keydown.enter="saveRename"
          />
        </div>
      </ModalDialogForm>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { browserSupportsWebAuthn } from '@simplewebauthn/browser'
import {
  deletePasskey,
  getPasskeys,
  registerPasskey,
  renamePasskey,
} from '~/services/passkeys'
import { ServiceError } from '~/services/errors'
import { addNotification } from '~/cynqueModules/addNotification'
import {
  getPrimaryHeaderClass,
  getTertiaryHeaderClass,
} from '~/cynqueModules/componentHelpers/tableCard/headerBandClass'
import { isDarkTheme } from '~/cynqueModules/nuxtAccessors'
import { describeWebAuthnError } from '~/cynqueModules/webAuthnErrors'
import { setShowModalDialog } from '~/cynqueModules/setShowModalDialog'
import { suggestPasskeyLabel } from '~/cynqueModules/suggestPasskeyLabel'
import { formatDateTimeStringDateOnly } from '~/cynqueModules/utils/formatDateTimeStringDateOnly'
import { useModalDialogStore } from '~/stores/modalDialog'
import type { IPasskey } from '~/types/passkey/models'

const mn = '[components/passkeys/PasskeysDialog.vue]'

// The api's limit (PasskeyLabelUpdate: 1–100 characters after trimming).
const MAX_LABEL_LENGTH = 100

export default defineComponent({
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      MAX_LABEL_LENGTH,
      // 30px wider than the dialogs it opens -- Rename here and Delete's
      // confirmation (the app-wide ModalDialog, fixed at 580) -- as an
      // EntityDialog (560) is wider than its subentity dialog (530).
      maxWidth: 610,
      renameMaxWidth: 580,
      passkeys: [] as IPasskey[],
      isLoading: true,
      isAdding: false,
      isDeleting: false,
      isRenaming: false,
      isSavingRename: false,
      renameTarget: null as IPasskey | null,
      renameLabel: '',
      canCreatePasskey: browserSupportsWebAuthn(),
    }
  },
  computed: {
    isBusy(): boolean {
      return this.isAdding || this.isDeleting || this.isSavingRename
    },
    // The tier is chosen by the GROUND, not by name. The dark ladder is tuned
    // against the #111 page; this table sits on the dialog's #333 card, where
    // SECONDARY's band measured 1.10:1 and all but vanished. PRIMARY's lands at
    // 1.80:1 there -- about what SECONDARY gets on the page (1.65) -- with the
    // text colour tuned for that band. Light is TERTIARY, chosen by eye over
    // SECONDARY (2026-10-29).
    headerClass(): string {
      return isDarkTheme()
        ? getPrimaryHeaderClass(true)
        : getTertiaryHeaderClass(false)
    },
  },
  watch: {
    // Loads on every OPEN, not in mounted(): the layout mounts this once at app
    // start, where a mounted() fetch would list passkeys on every page load.
    // Reloading per open also picks up a passkey the post-sign-in prompt
    // created meanwhile.
    async modelValue(isOpen: boolean) {
      if (isOpen) {
        await this.load()
      }
    },
  },
  methods: {
    close() {
      this.$emit('update:modelValue', false)
    },
    formatDate(value: string) {
      return formatDateTimeStringDateOnly(value)
    },
    notifyFailure(e: unknown, operation: 'create' | 'get') {
      const message = describeWebAuthnError(e, operation)
      if (message) {
        addNotification({
          type: 'warning',
          message,
          statusCode:
            e instanceof ServiceError && e.statusCode
              ? e.statusCode
              : undefined,
        })
      }
    },
    async load() {
      const fn = `${mn} load |`
      this.isLoading = true
      try {
        this.passkeys = await getPasskeys()
      } catch (e) {
        console.warn(`${fn} Could not load passkeys.`, e)
        this.notifyFailure(e, 'get')
      } finally {
        this.isLoading = false
      }
    },
    async addPasskey() {
      this.isAdding = true
      try {
        const passkey = await registerPasskey(suggestPasskeyLabel())
        this.passkeys = [...this.passkeys, passkey]
        addNotification({
          type: 'success',
          message: `Passkey "${passkey.label}" added.`,
        })
      } catch (e) {
        this.notifyFailure(e, 'create')
      } finally {
        this.isAdding = false
      }
    },
    startRename(passkey: IPasskey) {
      this.renameTarget = passkey
      this.renameLabel = passkey.label
      this.isRenaming = true
    },
    cancelRename() {
      this.isRenaming = false
    },
    async saveRename() {
      const target = this.renameTarget
      const label = this.renameLabel.trim()
      if (!target || !label || this.isSavingRename) {
        return
      }
      this.isSavingRename = true
      try {
        const renamed = await renamePasskey(target.id, label)
        this.passkeys = this.passkeys.map((p) =>
          p.id === renamed.id ? renamed : p
        )
        this.isRenaming = false
      } catch (e) {
        this.notifyFailure(e, 'get')
      } finally {
        this.isSavingRename = false
      }
    },
    async confirmDelete(passkey: IPasskey) {
      await setShowModalDialog({
        callingMn: `${mn} confirmDelete`,
        title: 'Delete passkey',
        message:
          `Delete "${passkey.label}"? You won't be able to sign in with it` +
          ' again, and anywhere it is signed in now will be signed out.',
        okButtonCaptionVerbOverride: 'DELETE',
        onOkCallback: this.deleteConfirmed,
        onOkCallbackData: passkey,
      })
    },
    async deleteConfirmed(passkey: IPasskey) {
      this.isDeleting = true
      useModalDialogStore().setIsProcessing(true)
      try {
        await deletePasskey(passkey.id)
        this.passkeys = this.passkeys.filter((p) => p.id !== passkey.id)
      } catch (e) {
        // A 409 here is the api refusing to remove the user's last way in; its
        // message says so.
        this.notifyFailure(e, 'get')
      } finally {
        this.isDeleting = false
        useModalDialogStore().setIsProcessing(false)
        useModalDialogStore().setShowModalDialog(false)
      }
    },
  },
})
</script>

<style scoped>
/* The table sits on the dialog card, whose colour is #333 in dark mode;
   VTable's own `surface` fill would paint a darker block on it. */
.pkd-table {
  background: transparent;
  /* Header at the embeddable TableCards' 32px (a five-row list in a dialog
     wants no more); rows at TableCard's default 48px. */
  --v-table-header-height: 32px;
  --v-table-row-height: 48px;
}
.pkd-table :deep(table) {
  table-layout: fixed;
}
.pkd-date-col {
  width: 112px;
}
/* Narrower than the dialog, centred in it (mx-auto). The card is a flex
   column, where auto margins shrink an unsized box to its content, so the
   width is explicit. */
.pkd-rename-field {
  width: 100%;
  max-width: 360px;
}
.pkd-synced-col {
  width: 88px;
}
/* The 36px kebab plus 4px each side. */
.pkd-kebab-col {
  width: 44px;
}
.pkd-table > .v-table__wrapper > table > thead > tr > th.pkd-kebab-cell,
.pkd-table > .v-table__wrapper > table > tbody > tr > td.pkd-kebab-cell {
  padding: 0 4px;
}
/* A long name wraps rather than widening its column. */
.pkd-table td {
  overflow-wrap: anywhere;
}
/* TableCard's header type, 12px/900. Same selector shape as TableCard's rule,
   which is what outranks Vuetify's own th weight and size. */
.pkd-table > .v-table__wrapper > table > thead > tr > th {
  font-weight: 900;
  font-size: 12px;
}
</style>
